// Рендер одной строки лога — по той же схеме, что печатает claude code в
// терминале: колонка глифа шириной в два знака, дальше текст. Глиф берётся из
// line.gutter, а если его нет — дефолт по kind (PROTOCOL §3). Пустая строка
// gutter="" означает продолжение абзаца агента: глифа нет, отступ тот же.
//
// Строка с rows — это вывод инструмента, даже если пришла с kind="warn"
// (ошибка выполнения). Раньше такие строки уходили в ветку текста, где текста
// нет, и в логе оставался голый «⎿» без единой строки вывода.

import { memo } from 'react'
import { DEFAULT_GUTTER } from '../protocol'
import type { LogLine } from '../store/logStore'
import type { PermissionOptions } from '../transport/ws'
import { inline } from './inline'
import PermissionCard from './PermissionCard'
import QuestionCard from './QuestionCard'
import ToolBlock from './ToolBlock'
import VoiceLine from './VoiceLine'
import AttachRow from './cli/AttachRow'
import BashBlock from './cli/BashBlock'
import CmdBlock from './cli/CmdBlock'
import PasteText from './cli/PasteText'

interface Props {
  sessionId: string
  line: LogLine
  onRespond(id: string, behavior: 'allow' | 'deny', opts?: PermissionOptions): void
}

function LineView({ sessionId, line, onRespond }: Props) {
  // вывод слэш-команды панели и `!`-команда — свои блоки (README §2а, ui-plan п.8)
  if (line.kind === 'cmd') return <CmdBlock line={line} />
  if (line.kind === 'bash') return <BashBlock sessionId={sessionId} line={line} />
  if (line.kind === 'voice' && line.voiceId) {
    return <VoiceLine sessionId={sessionId} voiceId={line.voiceId} text={line.text ?? ''} />
  }
  if (line.kind === 'perm' && line.perm?.toolName === 'AskUserQuestion') {
    return <QuestionCard sessionId={sessionId} perm={line.perm} onRespond={onRespond} />
  }
  if (line.kind === 'perm' && line.perm) {
    return <PermissionCard sessionId={sessionId} line={line} onRespond={onRespond} />
  }
  // Сам вызов AskUserQuestion — это JSON с вопросами; консоль его не печатает,
  // вопрос целиком показывает карточка ниже.
  if (line.kind === 'tool' && line.tool === 'AskUserQuestion') return null
  if (line.kind === 'tool' || line.kind === 'result' || (line.rows?.length ?? 0) > 0) {
    return <ToolBlock sessionId={sessionId} line={line} />
  }

  const glyph = line.gutter ?? DEFAULT_GUTTER[line.kind]
  // Абзац начинается — перед ним пустая строка, как в консоли; продолжение
  // абзаца идёт вплотную.
  const head = glyph !== ''
  // Реплика с вложениями: ряд превью над текстом (README §2в п.5)
  if ((line.kind === 'me' || line.kind === 'agent') && line.attachments?.length) {
    return (
      <div className={`t-row t-row--${line.kind} t-row--head`}>
        <span className="t-gut">{glyph}</span>
        <span className="t-txt">
          <AttachRow sessionId={sessionId} items={line.attachments} />
          {line.text && line.pastes?.length
            ? <PasteText text={line.text} pastes={line.pastes} keyPrefix={String(line.seq)} />
            : line.text ? inline(line.text, String(line.seq)) : null}
        </span>
      </div>
    )
  }
  return (
    <div className={`t-row t-row--${line.kind}${head ? ' t-row--head' : ''}`}>
      <span className="t-gut">{glyph}</span>
      <span className="t-txt">
        {line.kind === 'me' && line.pastes?.length
          ? <PasteText text={line.text ?? ''} pastes={line.pastes} keyPrefix={String(line.seq)} />
          : inline(line.text ?? '', String(line.seq))}
        {line.partial ? <i className="t-caret" /> : null}
      </span>
    </div>
  )
}

export default memo(LineView)
